import { cn } from '@/lib/utils'
import { Link } from '@tanstack/react-router'
import { Button } from './ui/button'
import { Drawer, DrawerClose, DrawerContent, DrawerTrigger } from './ui/drawer'
import { HouseIcon, MenuIcon } from 'lucide-react'

interface NavbarProps {
  isLight: boolean
  hideOnMobile?: boolean
}

const addAccommodationClassName =
  'inline-flex items-center gap-2 rounded-xl bg-[#FFF0F3] font-semibold whitespace-nowrap text-[#C8102E] transition-colors duration-150 hover:bg-[#FFE1E8] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C8102E] active:bg-[#FFE1E8] motion-reduce:transition-none'

function Navbar({ isLight }: NavbarProps) {
  const navLinks = [
    { to: '/ubytovani/hledat', label: 'Nabídky ubytování' },
    { to: '/', hash: 'about', label: 'Jak to funguje' },
    { to: '/inspirace', label: 'Inspirace' },
    { to: '/partner', label: 'Pro ubytovatele', highlighted: true },
    { to: '/kontakt', label: 'Kontakt' },
  ]

  return (
    <div className={cn(!isLight && 'bg-slate-100')}>
      <nav
        className={cn(
          'z-10 container mx-auto flex w-full flex-nowrap content-center items-center justify-between py-4 md:py-8',
        )}
      >
        {/* Mobile: Hamburger menu with drawer */}
        <Drawer>
          <DrawerTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn('lg:hidden', isLight ? 'text-white hover:bg-white/10' : 'text-slate-800')}
              aria-label="Open menu"
            >
              <MenuIcon className="h-6 w-6" />
            </Button>
          </DrawerTrigger>
          <DrawerContent className="max-h-[calc(100dvh-1.5rem)]">
            <nav className="flex min-h-0 flex-col overflow-y-auto px-4 pb-6">
              <div className="flex flex-col gap-2">
                {navLinks
                  .filter((link) => !link.highlighted)
                  .map((link) => (
                    <DrawerClose key={link.to + (link.hash || '')} asChild>
                      <Link
                        to={link.to}
                        hash={link.hash}
                        className="rounded-lg px-4 py-3 text-lg font-medium text-slate-700 hover:bg-slate-100"
                      >
                        {link.label}
                      </Link>
                    </DrawerClose>
                  ))}
              </div>
              <div className="mx-2 mt-4 border-t border-slate-200 pt-5">
                <DrawerClose asChild>
                  <Link
                    to="/partner"
                    className={cn(addAccommodationClassName, 'min-h-[52px] w-full justify-center px-[18px] text-base')}
                  >
                    <HouseIcon className="size-4" aria-hidden="true" />
                    Pro ubytovatele
                  </Link>
                </DrawerClose>
              </div>
            </nav>
          </DrawerContent>
        </Drawer>

        {/* Logo + claim: Left on desktop, centered on mobile */}
        <div className="-ml-2 flex flex-col items-center lg:mr-auto lg:ml-0 lg:flex-row lg:items-center">
          <Link to="/">
            <img src="/img/logo_matevolno.svg" alt="logo" className="-my-5 -mb-7 scale-75 md:scale-100 lg:-ml-5" />
          </Link>
          {/* Kept out of snippet selection — as the first text on every page
              it was what Google put in the SERP instead of the description. */}
          <span
            data-nosnippet
            className={cn(
              'mt-1 text-center text-[11px] leading-tight font-medium tracking-tight whitespace-nowrap lg:mt-0 lg:-ml-5 lg:max-w-[10rem] lg:border-l lg:pl-3 lg:text-left lg:text-xs lg:whitespace-normal',
              isLight ? 'text-white/90 lg:border-white/30' : 'text-slate-500 lg:border-slate-300',
            )}
          >
            <span className="lg:block">Last-minute úniky</span> <span className="lg:block">na příštích 14 dní.</span>
          </span>
        </div>

        {/* Mobile: Placeholder for user icon (right side) */}
        <div className="h-10 w-10 lg:hidden" />

        {/* Desktop Navigation */}
        <div className="hidden w-full items-center justify-end lg:flex lg:w-auto" id="navbar-cta">
          <ul
            className={cn(
              'mt-4 flex flex-col p-4 font-medium lg:mt-0 lg:flex-row lg:items-center lg:space-x-8 lg:p-0 lg:whitespace-nowrap xl:space-x-12',
              isLight ? 'text-white' : 'text-slate-600',
            )}
          >
            {navLinks.map((link) => (
              <li key={link.to + (link.hash || '')}>
                {link.highlighted ? (
                  <Link to={link.to} className={cn(addAccommodationClassName, 'px-[18px] py-3')}>
                    <HouseIcon className="size-4" aria-hidden="true" />
                    {link.label}
                  </Link>
                ) : (
                  <Link
                    to={link.to}
                    hash={link.hash}
                    aria-current="page"
                    className={cn(
                      "relative inline-block after:absolute after:-bottom-0.5 after:left-0 after:h-0.5 after:w-0 after:bg-black after:transition-all after:duration-300 after:content-[''] hover:after:w-full",
                      isLight && 'after:bg-white',
                    )}
                  >
                    {link.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </div>
  )
}

export default Navbar
